import { useState } from 'react';
import { Lock, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Reveal } from '@/components/Reveal';
import { GALLERY_DETAIL_PAGE } from '@/constants/testIds';

/** Access-code gate shown for a private gallery the visitor hasn't unlocked. */
export const GalleryLockedPanel = ({ onUnlock }) => {
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accessCode.trim()) return;
    setBusy(true);
    setError('');
    try {
      await onUnlock(accessCode.trim());
    } catch (err) {
      setError('Incorrect access code. Please check your invite and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Reveal>
      <div className="mx-auto max-w-md rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-8 sm:p-10 text-center" data-testid={GALLERY_DETAIL_PAGE.lockedPanel}>
        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))] mb-5">
          <Lock className="h-5 w-5" />
        </span>
        <h2 className="font-display text-2xl tracking-[0.03em] text-[hsl(var(--lxra-fg))]">Private Gallery</h2>
        <p className="mt-2 text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">
          This gallery is private. Enter the access code from your invite to view and download your photos.
        </p>
        <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-3">
          <Input
            type="text"
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            placeholder="Access code"
            aria-label="Access code"
            autoComplete="off"
            data-testid={GALLERY_DETAIL_PAGE.passcodeInput}
            className="bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] text-center tracking-[0.15em] uppercase placeholder:text-[hsl(var(--lxra-muted))] focus-visible:ring-[hsl(var(--lxra-gold))] h-11"
          />
          {error && (
            <p className="text-xs text-red-400" data-testid={GALLERY_DETAIL_PAGE.passcodeError}>{error}</p>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid={GALLERY_DETAIL_PAGE.passcodeSubmit}
            className="h-11 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] disabled:opacity-60 text-xs uppercase tracking-[0.1em] font-semibold"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Unlock Gallery'}
          </Button>
        </form>
      </div>
    </Reveal>
  );
};
